import { promises as fs } from "node:fs";
import http from "node:http";
import { aggregate, isRangeKey, type Overview } from "./aggregate.ts";
import type { ScanResult, SessionStore } from "./parser.ts";

const INDEX_HTML_URL = new URL("../public/index.html", import.meta.url);

interface ServerState {
  store: SessionStore;
  scan: ScanResult;
  scannedAt: number;
}

function sendJson(res: http.ServerResponse, status: number, body: unknown): void {
  const payload = JSON.stringify(body);
  res.writeHead(status, {
    "Content-Type": "application/json; charset=utf-8",
    "Cache-Control": "no-cache",
  });
  res.end(payload);
}

export interface StatsServer {
  server: http.Server;
  /** Rescan the sessions directory and update in-memory records. */
  refresh: () => Promise<ScanResult>;
}

export async function createStatsServer(store: SessionStore): Promise<StatsServer> {
  const indexHtml = await fs.readFile(INDEX_HTML_URL, "utf8");
  const state: ServerState = {
    store,
    scan: { records: [], fileCount: 0, parsedFiles: 0, elapsedMs: 0 },
    scannedAt: 0,
  };

  const refresh = async (): Promise<ScanResult> => {
    state.scan = await state.store.scan();
    state.scannedAt = Date.now();
    return state.scan;
  };
  await refresh();

  const server = http.createServer((req, res) => {
    const url = new URL(req.url ?? "/", "http://localhost");
    const pathname = url.pathname;

    if (req.method === "GET" && (pathname === "/" || pathname === "/index.html")) {
      res.writeHead(200, {
        "Content-Type": "text/html; charset=utf-8",
        "Cache-Control": "no-cache",
      });
      res.end(indexHtml);
      return;
    }

    if (req.method === "GET" && pathname === "/api/overview") {
      const rangeParam = url.searchParams.get("range") ?? "day";
      if (!isRangeKey(rangeParam)) {
        sendJson(res, 400, { error: `invalid range: ${rangeParam}` });
        return;
      }
      const overview: Overview = aggregate(state.scan.records, rangeParam);
      sendJson(res, 200, {
        ...overview,
        scan: {
          dir: state.store.dir,
          fileCount: state.scan.fileCount,
          recordCount: state.scan.records.length,
          scannedAt: state.scannedAt,
        },
      });
      return;
    }

    if (req.method === "POST" && pathname === "/api/refresh") {
      refresh()
        .then((scan) =>
          sendJson(res, 200, {
            fileCount: scan.fileCount,
            parsedFiles: scan.parsedFiles,
            recordCount: scan.records.length,
            elapsedMs: scan.elapsedMs,
            scannedAt: state.scannedAt,
          }),
        )
        .catch((error: unknown) =>
          sendJson(res, 500, { error: error instanceof Error ? error.message : String(error) }),
        );
      return;
    }

    sendJson(res, 404, { error: "not found" });
  });

  return { server, refresh };
}
